export const PRE_CALL_GUARDRAIL_STORAGE_KEY = 'pre_call_guardrails_v1'

/**
 * Safety valve. An outstanding pre-call form blocks the interpreter from receiving calls and
 * from logging out, and the only way to clear it is a successful
 * `PUT /PreCalls/questions-transaction`. If that endpoint were ever persistently unavailable
 * the interpreter would be unable to work at all — a reload does not help, because the record
 * lives in localStorage.
 *
 * After this many consecutive failed submissions the record stops enforcing. The form stays
 * restorable so the answers can still be sent once the endpoint recovers; it simply no longer
 * locks the interpreter out. Only genuine submit attempts count, so this cannot be waited out.
 */
export const MAX_PRECALL_SUBMIT_ATTEMPTS = 10

export interface GuardrailQAItem {
  code: string
  question: string
  answer: string
}

export interface GuardrailCallData {
  callId: string
  callHeaderId: string
  contactId?: string
  caller?: string
  language?: string
  channel?: string
  type?: string
  timestamp?: string
  action?: string
  isClosed?: boolean
  showPreCallQuestionsAlert?: boolean
  preCallQA?: GuardrailQAItem[]
}

export interface CallGuardrailRecord {
  callHeaderId: string
  callId: string
  callData: GuardrailCallData
  qaDraft: GuardrailQAItem[]
  note: string
  hasPreCallQuestions: boolean
  preCallSubmitted: boolean
  feedbackSubmitted: boolean
  isClosed: boolean
  /** Consecutive failed pre-call submissions. Only mutated via recordPreCallSubmitFailure. */
  failedSubmitAttempts: number
  updatedAt: number
}

export type CallGuardrailPatch = Partial<
  Pick<
    CallGuardrailRecord,
    | 'qaDraft'
    | 'note'
    | 'hasPreCallQuestions'
    | 'preCallSubmitted'
    | 'feedbackSubmitted'
    | 'isClosed'
  >
>

function readRecords(): CallGuardrailRecord[] {
  try {
    const stored = localStorage.getItem(PRE_CALL_GUARDRAIL_STORAGE_KEY)
    if (!stored) return []
    const records = JSON.parse(stored)
    return Array.isArray(records) ? records : []
  } catch {
    return []
  }
}

function writeRecords(records: CallGuardrailRecord[]): void {
  if (records.length === 0) {
    localStorage.removeItem(PRE_CALL_GUARDRAIL_STORAGE_KEY)
    return
  }
  localStorage.setItem(PRE_CALL_GUARDRAIL_STORAGE_KEY, JSON.stringify(records))
}

export function getCallGuardrails(): CallGuardrailRecord[] {
  return readRecords()
}

export function getCallGuardrail(callHeaderId: string | number): CallGuardrailRecord | undefined {
  const id = String(callHeaderId)
  return readRecords().find((record) => record.callHeaderId === id)
}

/** Tolerates records written before failedSubmitAttempts existed. */
function readAttempts(record?: Pick<CallGuardrailRecord, 'failedSubmitAttempts'>): number {
  const attempts = Number(record?.failedSubmitAttempts)
  return Number.isFinite(attempts) && attempts > 0 ? attempts : 0
}

/**
 * True once the interpreter has tried and failed to submit enough times that keeping them
 * blocked is doing more harm than the missing answers. Fails open by design.
 */
export function hasExhaustedSubmitAttempts(record: CallGuardrailRecord): boolean {
  return readAttempts(record) >= MAX_PRECALL_SUBMIT_ATTEMPTS
}

/** Unsubmitted pre-call questions that still block logout. */
export function getPendingPreCallGuardrails(): CallGuardrailRecord[] {
  return readRecords().filter(
    (record) =>
      record.hasPreCallQuestions &&
      !record.preCallSubmitted &&
      !hasExhaustedSubmitAttempts(record)
  )
}

/**
 * A finished call whose pre-call questions were never submitted. This is what forces the
 * interpreter offline, so it must block further incoming calls and must survive a purge —
 * until the submit attempts are exhausted, at which point it stops enforcing and becomes
 * purgeable again.
 */
export function isBlockingGuardrail(record: CallGuardrailRecord): boolean {
  return (
    record.isClosed &&
    record.hasPreCallQuestions &&
    !record.preCallSubmitted &&
    !hasExhaustedSubmitAttempts(record)
  )
}

export function getBlockingPreCallGuardrails(): CallGuardrailRecord[] {
  return readRecords().filter((record) => isBlockingGuardrail(record))
}

/**
 * Count one failed pre-call submission. Call this for every unsuccessful attempt — both a
 * rejected response and a thrown request — so a persistently failing endpoint eventually
 * releases the interpreter instead of stranding them.
 */
export function recordPreCallSubmitFailure(
  callHeaderId: string | number
): CallGuardrailRecord | undefined {
  const id = String(callHeaderId)
  const records = readRecords()
  const index = records.findIndex((record) => record.callHeaderId === id)
  if (index < 0) return undefined

  records[index] = {
    ...records[index],
    failedSubmitAttempts: readAttempts(records[index]) + 1,
    updatedAt: Date.now(),
  }
  writeRecords(records)
  return records[index]
}

export function getRestorableCallGuardrails(): CallGuardrailRecord[] {
  return readRecords().filter(
    (record) =>
      (record.hasPreCallQuestions && !record.preCallSubmitted) ||
      (record.isClosed && !record.feedbackSubmitted)
  )
}

function hasAnsweredDraft(items: GuardrailQAItem[] | undefined): boolean {
  return Boolean(items?.some((item) => Boolean(item.answer?.trim())))
}

export function upsertCallGuardrail(
  callData: GuardrailCallData,
  patch: CallGuardrailPatch = {}
): CallGuardrailRecord {
  const callHeaderId = String(callData.callHeaderId)
  const records = readRecords()
  const existingIndex = records.findIndex((record) => record.callHeaderId === callHeaderId)
  const existing = existingIndex >= 0 ? records[existingIndex] : undefined

  // Prefer answered drafts over empty/unanswered refreshes from SignalR/API.
  const nextQaDraft =
    patch.qaDraft === undefined
      ? existing?.qaDraft ?? []
      : hasAnsweredDraft(existing?.qaDraft) && !hasAnsweredDraft(patch.qaDraft)
        ? existing!.qaDraft
        : patch.qaDraft

  const record: CallGuardrailRecord = {
    callHeaderId,
    callId: String(callData.callId),
    callData: {...existing?.callData, ...callData, callHeaderId},
    qaDraft: nextQaDraft,
    note: patch.note ?? existing?.note ?? '',
    hasPreCallQuestions:
      patch.hasPreCallQuestions ?? existing?.hasPreCallQuestions ?? false,
    // Never downgrade a completed pre-call submission on event replay.
    preCallSubmitted:
      Boolean(existing?.preCallSubmitted) ||
      Boolean(patch.preCallSubmitted ?? false),
    feedbackSubmitted:
      Boolean(existing?.feedbackSubmitted) ||
      Boolean(patch.feedbackSubmitted ?? false),
    isClosed:
      Boolean(existing?.isClosed) ||
      Boolean(patch.isClosed ?? callData.isClosed ?? false),
    // Preserved across event replay, like preCallSubmitted — a SignalR refresh must not
    // silently restore enforcement by resetting the interpreter's failed attempts.
    failedSubmitAttempts: readAttempts(existing),
    updatedAt: Date.now(),
  }

  if (existingIndex >= 0) records[existingIndex] = record
  else records.push(record)
  writeRecords(records)
  return record
}

export function updateCallGuardrail(
  callHeaderId: string | number,
  patch: CallGuardrailPatch
): CallGuardrailRecord | undefined {
  const id = String(callHeaderId)
  const records = readRecords()
  const index = records.findIndex((record) => record.callHeaderId === id)
  if (index < 0) return undefined

  records[index] = {
    ...records[index],
    ...patch,
    updatedAt: Date.now(),
  }
  writeRecords(records)
  return records[index]
}

export function removeCallGuardrail(callHeaderId: string | number): void {
  const id = String(callHeaderId)
  writeRecords(readRecords().filter((record) => record.callHeaderId !== id))
}

/**
 * Drop persisted active-call modals except the current call (next call supersedes prior UI).
 *
 * Blocking records are retained. A record with unanswered pre-call questions is the reason
 * the interpreter was forced offline; deleting it silently discarded their unsubmitted
 * answers and erased the state the incoming-call guards rely on, so a queued call arriving
 * right after a call ended could purge the block and present itself anyway.
 */
export function purgeCallGuardrailsExcept(callHeaderId: string | number): void {
  const keepId = String(callHeaderId)
  writeRecords(
    readRecords().filter(
      (record) => record.callHeaderId === keepId || isBlockingGuardrail(record)
    )
  )
}

export function clearCallGuardrails(): void {
  localStorage.removeItem(PRE_CALL_GUARDRAIL_STORAGE_KEY)
}
